import { GridCellModesModel, GridEventListener } from "@mui/x-data-grid-premium";
import React from "react";
import { SelectedCellParams } from "shared/components";
import { GridColumns } from "shared/types";

type UseManualSaveProps<T> = {
    handleIsEditableRow?: (rowId: string) => boolean;
    isEnabled: boolean,
    columnConfig: GridColumns<T>;
    editButtonText?: string;
}

type UseManualSaveReturnProps = {
    selectedCellParams: SelectedCellParams | null;
    cellModesModel: GridCellModesModel;
    setCellModesModel: (value: GridCellModesModel) => void;
    cellMode: 'view' | 'edit';
    handleCellFocus: (event: React.FocusEvent<HTMLDivElement>) => void;
    handleCellKeyDown: GridEventListener<"cellKeyDown">;
    setSelectedCellParams: React.Dispatch<React.SetStateAction<SelectedCellParams | null>>;
} | undefined

export default function useManualSave<T>({isEnabled, handleIsEditableRow, columnConfig}: UseManualSaveProps<T>): UseManualSaveReturnProps {
    const [selectedCellParams, setSelectedCellParams] = React.useState<SelectedCellParams | null>(null);
    const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});

    const handleCellFocus = React.useCallback(
        (event: React.FocusEvent<HTMLDivElement>) => {
            const row = event.currentTarget.parentElement;
            const id = row!.dataset.id!;
            const field = event.currentTarget.dataset.field!;
            const isEditableColumn = columnConfig.find(c => c.field === field)?.editable;
    
            setSelectedCellParams({ id, field, isEditableField: !!isEditableColumn && (handleIsEditableRow ? handleIsEditableRow(id) : true) });
        },
        [handleIsEditableRow, columnConfig],
    );

    const cellMode = React.useMemo(() => {
        if (!selectedCellParams) {
        return 'view';
        }
        const { id, field } = selectedCellParams;
        return cellModesModel[id]?.[field]?.mode || 'view';
    }, [cellModesModel, selectedCellParams]);

    const handleCellKeyDown = React.useCallback<GridEventListener<'cellKeyDown'>>(
        (params, event) => {
        if (cellMode === 'edit') {
            // Prevents calling event.preventDefault() if Tab is pressed on a cell in edit mode
            event.defaultMuiPrevented = true;
        }
        },
        [cellMode],
    );

    if(!isEnabled) return undefined

    return {
        handleCellFocus,
        cellMode,
        handleCellKeyDown,
        cellModesModel,
        setCellModesModel,
        selectedCellParams,
        setSelectedCellParams
    }
}